import { notification } from "antd";
import { ColumnProps } from "antd/lib/table";
import React, { useRef, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { DigitalShareAppType } from "../../../../Enums/DigitalShareAppType";
import { DigitalShareItemType } from "../../../../Enums/DigitalShareItemType";
import { RenderType } from "../../../../Enums/RenderType";
import { RetailMatchMetricLevel } from "../../../../Enums/RetailMatchMetricLevel";
import { IBrandOverviewTableRecord } from "../../../../Models/IBrandOverviewTableRecord";
import { IBrandSummaryData } from "../../../../Models/IBrandSummaryData";
import { IRetailMatchJobResult } from "../../../../Models/IRetailMatch";
import {
    retailMatchJobSelector,
    retailMatchLongAddressSelector,
} from "../../../../Reducers/Selectors/RetailMatchSelectors";
import { getDateBackVerbiage } from "../../../../Util/RetailMatch/RetailMatchUtil";
import {
    getDigitalShareMapImage,
    getIsDigitalShare,
} from "../../../../Util/ScoutDigitalShareUtil";
import {
    isOutputSelected,
    mapSnapshots,
    registerElement,
    registerRef,
} from "../../../Common/DigitalShare/DigitalShareGenerator";
import {
    getBrandSummaryData,
    brandLinkURL,
    MAX_BRAND_COUNT_OPTIMIZATION_MODE,
    rankDataSort,
    rankTableSort,
    TOO_FEW_RECORD_EXPLANATION,
} from "../Util/RetailMatchUtil";
import RetailMatchBreakoutItem from "./RetailMatchBreakoutItem";
import RetailMatchIntroTextHeader from "./RetailMatchIntroTextHeader";
import { StyledTable } from "./RetailMatchLocationsAnalyzed";

interface IRetailMatchMultiBrandIndexBreakoutProps {
    jobResult: IRetailMatchJobResult;
    isSharing: boolean;
    renderType: RenderType;
}

const brandSort = (
    a: IBrandOverviewTableRecord,
    b: IBrandOverviewTableRecord
) => {
    if (a.brandSummary.overview.brandName < b.brandSummary.overview.brandName) {
        return -1;
    } else if (
        a.brandSummary.overview.brandName > b.brandSummary.overview.brandName
    ) {
        return 1;
    } else {
        return 0;
    }
};

const custCountSort = (
    a: IBrandOverviewTableRecord,
    b: IBrandOverviewTableRecord
) => {
    return (
        a.brandSummary.overview.coreCustomerCountIndex
        - b.brandSummary.overview.coreCustomerCountIndex
    );
};

const custRatioSort = (
    a: IBrandOverviewTableRecord,
    b: IBrandOverviewTableRecord
) => {
    return (
        a.brandSummary.overview.coreCustomerPerHouseholdIndex
        - b.brandSummary.overview.coreCustomerPerHouseholdIndex
    );
};

const custSimSort = (
    a: IBrandOverviewTableRecord,
    b: IBrandOverviewTableRecord
) => {
    return (
        a.brandSummary.overview.coreCustomerCorrelationIndex
        - b.brandSummary.overview.coreCustomerCorrelationIndex
    );
};

const columns: ColumnProps<IBrandOverviewTableRecord>[] = [
    {
        title: <strong>Rank</strong>,
        dataIndex: "rank",
        key: "rank",
        width: "10%",
        sorter: rankTableSort,
    },
    {
        title: <strong>Brand</strong>,
        dataIndex: "brand",
        key: "brand",
        width: "30%",
        sorter: brandSort,
    },
    {
        title: <strong>Ideal Customer Count</strong>,
        dataIndex: "idealCustomerCount",
        key: "idealCustomerCount",
        sorter: custCountSort,
    },
    {
        title: <strong>Ideal Customer Ratio</strong>,
        dataIndex: "idealCustomerRatio",
        key: "idealCustomerRatio",
        sorter: custRatioSort,
    },
    {
        title: <strong>Ideal Customer Similarity</strong>,
        dataIndex: "idealCustomerSimilarity",
        key: "idealCustomerSimilarity",
        sorter: custSimSort,
    },
];

const NA = <div style={{ textAlign: "center" }}>N/A</div>;

const RetailMatchMultiBrandIndexBreakout = (props: IRetailMatchMultiBrandIndexBreakoutProps): JSX.Element => {
    const history = useHistory();

    const brandSummaryData: IBrandSummaryData[] = props.jobResult.job.brands.map(
        (brand) => {
            return getBrandSummaryData(brand, props.jobResult);
        }
    );

    const topN = brandSummaryData
        .sort(rankDataSort)
        .slice(0, MAX_BRAND_COUNT_OPTIMIZATION_MODE);

    const onRowClick = (row: IBrandOverviewTableRecord) => {
        row.brandSummary.tooFewRecords
            ? notification.open({ message: TOO_FEW_RECORD_EXPLANATION })
            : history.push(
                brandLinkURL(
                    history.location.pathname,
                    props.isSharing,
                    props.jobResult.job.jobId,
                    row.brandSummary.brand.brandId
                )
            );
        // : history.push(`${history.location.pathname}/${row.brandSummary.brand.brandId}`);
    };

    const tableData: IBrandOverviewTableRecord[] = topN
        .map((brandSummary, index) => {
            return {
                key: brandSummary.brand.brandId,
                brandSummary: brandSummary,
                rank: brandSummary.overview?.rank || index,
                brand: (
                    <>
                        <img
                            src={brandSummary.brand.logoPath}
                            style={{
                                width: "40px",
                                marginRight: "4px",
                            }}
                        />
                        <strong
                            style={{
                                fontSize: "17px",
                            }}
                        >
                            {brandSummary.brand.brandName}
                        </strong>
                    </>
                ),
                idealCustomerCount: brandSummary.tooFewRecords ? (
                    NA
                ) : (
                    <RetailMatchBreakoutItem
                        value={brandSummary.overview.coreCustomerCountIndex}
                        fontSize={13}
                        metricLevel={RetailMatchMetricLevel.Average}
                    />
                ),
                idealCustomerRatio: brandSummary.tooFewRecords ? (
                    NA
                ) : (
                    <RetailMatchBreakoutItem
                        value={brandSummary.overview.coreCustomerPerHouseholdIndex}
                        fontSize={13}
                        metricLevel={RetailMatchMetricLevel.Comparable}
                    />
                ),
                idealCustomerSimilarity: brandSummary.tooFewRecords ? (
                    NA
                ) : (
                    <RetailMatchBreakoutItem
                        value={brandSummary.overview.coreCustomerCorrelationIndex}
                        fontSize={13}
                        metricLevel={RetailMatchMetricLevel.Similarity}
                    />
                ),
            };
        })
        .sort(rankTableSort);

    const [tableDataCopy, setTableDataCopy]
        = useState<IBrandOverviewTableRecord[]>(tableData);
    const brandListRef = useRef();
    const jobResult = useSelector(retailMatchJobSelector);
    const longAddress = useSelector(retailMatchLongAddressSelector);

    useEffect(() => {
        if (
            getIsDigitalShare(props.renderType)
            && isOutputSelected(
                DigitalShareAppType.RetailMatch,
                DigitalShareItemType.BrandComparison
            )
        ) {
            const el = document.getElementById(
                "retail-match-overview-map"
            ) as HTMLElement;

            getDigitalShareMapImage(el, 310, 227).then((i) => {
                mapSnapshots.set("retail-match-overview-map", i);
                const columnsCopy = Array.from(columns, (c) => Object.assign({}, c));
                columnsCopy.forEach((c) => delete c.sorter);

                registerRef(
                    DigitalShareAppType.RetailMatch,
                    DigitalShareItemType.BrandComparison,
                    brandListRef
                );
                registerElement(
                    DigitalShareAppType.RetailMatch,
                    DigitalShareItemType.BrandComparison,
                    "",
                    <div>
                        <RetailMatchIntroTextHeader
                            address={longAddress}
                            isSharing={true}
                            multiBrand={true}
                            sampleCount={null}
                            brandName={null}
                            brand={null}
                            backDateVerbiage={getDateBackVerbiage(
                                jobResult.startDate,
                                jobResult.endDate
                            )}
                            showEditAddressModal={null}
                            marginBottom={"0"}
                        />
                        <StyledTable
                            columns={columnsCopy}
                            dataSource={tableDataCopy.slice(0, 5)}
                            pagination={false}
                        />
                    </div>
                );
            });
        }
    }, [props.renderType]);

    return (
        <StyledTable
            columns={columns}
            dataSource={tableData}
            pagination={false}
            onRowClick={(row: IBrandOverviewTableRecord) => onRowClick(row)}
            onChange={(pagination, filter, sorter, extra) =>
                setTableDataCopy(extra.currentDataSource as IBrandOverviewTableRecord[])
            }
            scroll={{ y: 385 }}
        />
    );
};

export default RetailMatchMultiBrandIndexBreakout;
